import { EncoderProtocol, MeterConfig } from '@ncss/models/dist/v3';

const RSSI_MAX = -55;
const RSSI_MIN = -120;

export function CheckInIntervalToHumanReadableStr(checkInInterval: number) {
  const hrs = (checkInInterval * 2) / 3600;
  if (hrs > 1) {
    return `${hrs} Hours`;
  } else if (hrs === 1) {
    return `${hrs} Hour`;
  } else {
    return `${hrs * 60} Minutes`;
  }
}

export function BleRssiToSignalStrength(val?: number): number {
  if (!val) {
    return 0;
  }
  if (val > RSSI_MAX) {
    return 100;
  } else if (val < RSSI_MIN) {
    return 0;
  }

  const percent = ((val + RSSI_MIN * -1) / (RSSI_MAX + RSSI_MIN * -1 - (RSSI_MIN + RSSI_MIN * -1))) * 100;
  return +percent.toFixed(0);
}

export function BleSignalStrengthToImgUrl(val: number) {
  if (val < 50) {
    return 'equipment/direct-connect-signal-poor.svg';
  } else if (val < 70) {
    return 'equipment/direct-connect-signal-fair.svg';
  } else if (val < 90) {
    return 'equipment/direct-connect-signal-good.svg';
  }
  return 'equipment/direct-connect-signal-excellent.svg';
}

export function EndDeviceRssiToImgUrl(val: number) {
  if (val > 168) {
    return 'equipment/direct-connect-signal-excellent.svg';
  } else if (val < 85) {
    return 'equipment/direct-connect-signal-poor.svg';
  }
  return 'equipment/direct-connect-signal-good.svg';
}

export function BleSignalStrengthToColor(val: number) {
  if (val < 50) {
    return 'yellow';
  } else if (val < 70) {
    return 'green';
  } else if (val < 90) {
    return 'blue';
  }
  return 'blue';
}

export function EndDeviceRssiToColor(val: number) {
  if (val > 168) {
    return 'blue';
  } else if (val < 85) {
    return 'yellow';
  }
  return 'green';
}

export function EndDeviceRssiToString(val: number) {
  if (val > 168) {
    return 'Excellent';
  } else if (val < 85) {
    return 'Poor';
  }
  return 'Good';
}

export const EncodedProtocolLabels: { [protocol: number]: string } = {};
EncodedProtocolLabels[EncoderProtocol.AMCO] = 'AMCO';
EncodedProtocolLabels[EncoderProtocol.NEPTUNE] = 'Neptune';
EncodedProtocolLabels[EncoderProtocol.SENSUS] = 'Sensus';
EncodedProtocolLabels[EncoderProtocol.ECO] = 'GWF ECO';

export interface MeterStatus {
  isOk: boolean;
  checkWiring?: boolean;
  label: string;
  description: string;
}
export type OverallStatus = MeterStatus & { meter1?: MeterStatus; meter2?: MeterStatus };

export function computeMeterOverallStatus(
  meter1: { type: MeterConfig; isOk: boolean; protocol: EncoderProtocol },
  meter2: { type: MeterConfig; isOk: boolean; protocol: EncoderProtocol }
): OverallStatus | undefined {
  const meter1Status = computeStatusForMeter(meter1);
  const meter2Status = computeStatusForMeter(meter2);
  if (!meter1Status) {
    return;
  }
  if (!meter2Status) {
    return {
      ...meter1Status,
      meter1: meter1Status,
    };
  }

  const status: OverallStatus = {
    isOk: false,
    label: '',
    description: '',
    meter1: meter1Status,
    meter2: meter2Status,
  };

  if (meter1.isOk && meter2.isOk) {
    if (meter1.type === MeterConfig.PULSE_IN && meter2.type === MeterConfig.PULSE_IN) {
      // Both Meters - Healthy - PULSE
      status.isOk = true;
      status.label = 'Meters Detected';
      status.description = 'Pulses have been detected within the last 24 hours on both meters.';
    } else if (meter1.type === MeterConfig.ENCODER_IN && meter2.type === MeterConfig.ENCODER_IN) {
      // Both Meters - Healthy - ENCODED
      const protocols: string[] = [];
      if (EncodedProtocolLabels[meter1.protocol]) {
        protocols.push(EncodedProtocolLabels[meter1.protocol]);
      }
      if (EncodedProtocolLabels[meter2.protocol]) {
        protocols.push(EncodedProtocolLabels[meter2.protocol]);
      }
      status.isOk = true;
      status.label = 'Communicating With Meters';
      status.description =
        'The Remote Reader can successfully communicate with both connected meters' + protocols.length
          ? ` (${protocols.join(' and ')})`
          : '';
    } else {
      // Both Meters - Healthy - PULSE AND ENCODED
      status.isOk = true;
      status.label = 'Meters Detected';
      status.description = 'The Remote Reader has successfully detected both connected meters.';
    }
  } else if (!meter1.isOk && meter2.isOk) {
    // Meter 1 - NOT Healthy
    if (meter1.type === MeterConfig.PULSE_IN) {
      status.isOk = false;
      status.label = 'No Usage Detected';
      status.description = 'No pulses detected within the last 24 hours on Meter 1.';
    } else {
      status.isOk = false;
      status.checkWiring = true;
      status.label = 'Check Wiring';
      status.description =
        'The Remote Reader could not communicate with the Meter 1. Refer to the "Wiring Guide" here or contact us at support@nextcenturymeters.com for assistance.';
    }
  } else if (meter1.isOk && !meter2.isOk) {
    // Meter 2 - NOT Healthy
    if (meter2.type === MeterConfig.PULSE_IN) {
      status.isOk = false;
      status.label = 'No Usage Detected';
      status.description = 'No pulses detected within the last 24 hours on Meter 2.';
    } else {
      status.isOk = false;
      status.checkWiring = true;
      status.label = 'Check Wiring';
      status.description =
        'The Remote Reader could not communicate with Meter 2. Refer to the "Wiring Guide" here or contact us at support@nextcenturymeters.com for assistance.';
    }
  } else {
    if (meter1.type === MeterConfig.PULSE_IN && meter2.type === MeterConfig.PULSE_IN) {
      // Both Meters - NOT Healthy - PULSE
      status.isOk = false;
      status.label = 'Meters Not Detected';
      status.description = 'No pulses have been detected within the last 24 hours on both meters.';
    } else if (meter1.type === MeterConfig.ENCODER_IN && meter2.type === MeterConfig.ENCODER_IN) {
      // Both Meters - NOT Healthy - ENCODED
      status.isOk = false;
      status.checkWiring = true;
      status.label = 'Check Wiring';
      status.description =
        'The Remote Reader could not communicate with either meter. Refer to the "Wiring Guide" here or contact us at support@nextcenturymeters.com for assistance.';
    } else {
      // Both Meters - NOT Healthy - PULSE AND ENCODED
      status.isOk = false;
      status.checkWiring = true;
      status.label = 'Check Wiring';
      status.description = 'The Remote Reader has not detected either meters.';
    }
  }
  return status;
}

export function computeStatusForMeter(meter: {
  type: MeterConfig;
  isOk: boolean;
  protocol: EncoderProtocol;
}): MeterStatus | undefined {
  if (meter.type === MeterConfig.PORT_DISABLED || meter.type === MeterConfig.PULSE_OUT) {
    return;
  }
  if (meter.isOk) {
    if (meter.type === MeterConfig.PULSE_IN) {
      return {
        isOk: true,
        label: 'Usage Detected',
        description: 'Pulses have been detected within the last 24 hours.',
      };
    } else {
      return {
        isOk: true,
        label: 'Communicating With Meter',
        description: `The Remote Reader can successfully communicate with the connected ${
          EncodedProtocolLabels[meter.protocol]
        } meter.`,
      };
    }
  } else {
    if (meter.type === MeterConfig.PULSE_IN) {
      return {
        isOk: false,
        label: 'No Usage Detected',
        description: 'No pulses detected within the last 24 hours.',
      };
    } else {
      return {
        isOk: false,
        checkWiring: true,
        label: 'Check Wiring',
        description:
          'The Remote Reader could not communicate with the connected meter. Refer to the "Wiring Guide" here or contact us at support@nextcenturymeters.com for assistance.',
      };
    }
  }
}
